import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useSkillStoreVisibility } from '../hooks/useSkillStoreVisibility';

import { LANDING_TABS, LandingSkillChoice, withoutSkillBuilder } from './landingChoices';
import { SkillSearchPanel, useLandingSkillOptions, useSkillSearch } from './LandingSkillSearch';

interface LandingModeTabsProps {
  value: LandingSkillChoice;
  onChange: (choice: LandingSkillChoice) => void;
}

const TAB_ICONS: Record<string, string> = {
  chat: 'fa-comment',
  loop: 'fa-repeat',
  'website-builder': 'fa-globe',
  'saas-builder': 'fa-layer-group',
  'app-builder': 'fa-cube',
};

const ModeTab: React.FC<{
  choice: LandingSkillChoice;
  selected: boolean;
  onSelect: () => void;
}> = ({ choice, selected, onSelect }) => (
  <button
    type="button"
    role="tab"
    className={`landing-mode-tab${selected ? ' is-selected' : ''}`}
    aria-selected={selected}
    onClick={onSelect}
    title={choice.description}
    data-testid={`landing-mode-tab-${choice.id}`}
  >
    <i className={`fa-solid ${TAB_ICONS[choice.id] ?? 'fa-wand-magic-sparkles'}`} aria-hidden="true" />
    <span>{choice.displayName}</span>
  </button>
);

const useDismissOnOutsidePointer = (
  open: boolean,
  regionRef: React.RefObject<HTMLDivElement | null>,
  close: () => void,
) => {
  useEffect(() => {
    if (!open) return undefined;
    const dismiss = (event: MouseEvent) => {
      if (event.target instanceof Node && regionRef.current?.contains(event.target)) return;
      close();
    };

    document.addEventListener('pointerdown', dismiss);

    return () => document.removeEventListener('pointerdown', dismiss);
  }, [close, open, regionRef]);
};

/**
 * Website / SaaS / App / Chat / Skill tabs above the landing text box. The Skill tab opens the
 * reusable-skill search and shows the picked skill's name once one is chosen.
 */
// eslint-disable-next-line max-lines-per-function, max-statements
export const LandingModeTabs: React.FC<LandingModeTabsProps> = ({ value, onChange }) => {
  const skillStoreVisible = useSkillStoreVisibility();
  const { skills: storeSkills, unavailable } = useLandingSkillOptions();
  const skills = useMemo(
    () => skillStoreVisible ? storeSkills : withoutSkillBuilder(storeSkills),
    [skillStoreVisible, storeSkills],
  );
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const regionRef = useRef<HTMLDivElement>(null);
  const search = useSkillSearch(skills, searchOpen, searchQuery, skillStoreVisible);
  const closeSearch = React.useCallback(() => {
    setSearchOpen(false);
    setSearchQuery('');
  }, []);

  useDismissOnOutsidePointer(searchOpen, regionRef, closeSearch);

  const pickedSkill = value.kind === 'skill' ? value : null;
  const showSkillTab = skillStoreVisible || Boolean(pickedSkill);

  return (
    <div ref={regionRef} className="landing-mode-tabs" role="tablist" aria-label="What do you want to do?">
      {LANDING_TABS.map((choice) => (
        <ModeTab key={choice.id} choice={choice} selected={value.id === choice.id} onSelect={() => onChange(choice)} />
      ))}
      {showSkillTab && (
        <button
          type="button"
          role="tab"
          className={`landing-mode-tab landing-mode-tab--skill${pickedSkill ? ' is-selected' : ''}${searchOpen ? ' is-open' : ''}`}
          aria-selected={Boolean(pickedSkill)}
          onClick={() => setSearchOpen((open) => !open)}
          aria-expanded={searchOpen}
          aria-controls="landing-skill-search-results"
          title={pickedSkill ? `${pickedSkill.displayName}: click to change skill` : 'Pick a reusable skill'}
          data-testid="landing-skill-search-toggle"
        >
          <i className="fa-solid fa-wand-magic-sparkles" aria-hidden="true" />
          <span>{pickedSkill ? pickedSkill.displayName : 'Skill'}</span>
          <i className="fa-solid fa-chevron-down landing-mode-tab__caret" aria-hidden="true" />
        </button>
      )}
      {searchOpen && (
        <div className="landing-mode-tabs__search">
          <SkillSearchPanel
            choices={search.choices}
            query={searchQuery}
            searching={search.searching}
            unavailable={search.unavailable || unavailable}
            onQueryChange={setSearchQuery}
            onSelect={(choice) => {
              onChange(choice);
              closeSearch();
            }}
            onClose={closeSearch}
          />
        </div>
      )}
    </div>
  );
};
